import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import GameAnalytics from '../components/analytics/GameAnalytics';
import TeamAnalytics from '../components/analytics/TeamAnalytics';
import NCAABAnalyticsContent from '../components/analytics/NCAABAnalyticsContent';
import api from '../services/api';
import { getGamePredictions } from '../services/predictions';

// Extract short team name (e.g., "Chicago Bears" -> "Bears")
const getShortTeamName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.split(' ');
  // Return last word(s) as team name - handles "Trail Blazers", "Blue Jackets", etc.
  if (parts.length >= 3 && ['Trail', 'Blue', 'Red', 'Maple', 'Golden'].includes(parts[parts.length - 2])) {
    return parts.slice(-2).join(' ');
  }
  return parts[parts.length - 1] || fullName;
};

const GameAnalyticsPage = () => {
  const { sport, gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [players, setPlayers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playersError, setPlayersError] = useState(null);
  const [activeTab, setActiveTab] = useState('team'); // 'team' or 'players'
  const [playersLoaded, setPlayersLoaded] = useState(false);
  const [dayGames, setDayGames] = useState([]);

  // Fetch game summary (fast endpoint - team data, betting lines, no player stats)
  useEffect(() => {
    const fetchGameSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        setPlayersLoaded(false);
        setPlayers(null);

        // Fetch game summary from the fast endpoint
        // Note: /api prefix is already in the baseURL, don't duplicate it
        const response = await api.get(`/${sport}/games/${gameId}/summary`);

        if (response.data) {
          setGame(response.data);
        } else {
          setError('Game not found');
        }
      } catch (err) {
        console.error('Error fetching game summary:', err);
        if (err.response?.status === 404) {
          setError('Game not found');
        } else if (err.response?.status === 501) {
          setError(`Analytics not yet available for ${sport.toUpperCase()}`);
        } else {
          setError('Failed to load game analytics');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchGameSummary();
  }, [sport, gameId]);

  // Fetch other games on the same day for the game switcher
  useEffect(() => {
    if (!game?.date) return;

    const fetchDayGames = async () => {
      try {
        const gameDate = game.date.split('T')[0];
        const data = await getGamePredictions(sport, gameDate);
        const games = Array.isArray(data) ? data
          : data?.games || data?.data || [];
        setDayGames(games);
      } catch {
        setDayGames([]);
      }
    };

    fetchDayGames();
  }, [sport, game?.date]);

  // Fetch players data (heavier endpoint - called when Player Analysis tab is selected)
  const fetchPlayers = useCallback(async () => {
    if (playersLoaded || playersLoading) return;

    try {
      setPlayersLoading(true);
      setPlayersError(null);

      const response = await api.get(`/${sport}/games/${gameId}/players`);

      if (response.data) {
        setPlayers(response.data);
        setPlayersLoaded(true);
      }
    } catch (err) {
      console.error('Error fetching player analytics:', err);
      setPlayersError('Failed to load player analytics');
    } finally {
      setPlayersLoading(false);
    }
  }, [sport, gameId, playersLoaded, playersLoading]);

  // Load players when switching to players tab
  useEffect(() => {
    if (activeTab === 'players' && !playersLoaded && !playersLoading) {
      fetchPlayers();
    }
  }, [activeTab, playersLoaded, playersLoading, fetchPlayers]);

  // Auto-fetch players on mount for non-NCAAB sports (progressive loading)
  const isNCAABSport = sport === 'ncaa-b';
  useEffect(() => {
    if (game && !playersLoaded && !playersLoading && !isNCAABSport) {
      fetchPlayers();
    }
  }, [game, playersLoaded, playersLoading, isNCAABSport, fetchPlayers]);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Card className="p-8 text-center">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="h-8 w-48 bg-gray-700 rounded"></div>
            <div className="h-4 w-64 bg-gray-700 rounded"></div>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Card className="p-8 text-center">
          <p className="text-danger">{error || 'Game not found'}</p>
        </Card>
      </div>
    );
  }

  const isLive = game.status === 'in_progress' || game.status === 'In Progress';
  const isFinal = game.status === 'final' || game.status === 'Final' || game.status === 'completed' || game.status === 'Completed';
  const isNCAAB = sport === 'ncaa-b';

  const handleGameSwitch = (e) => {
    const newGameId = e.target.value;
    if (newGameId && newGameId !== gameId) {
      navigate(`/leagues/${sport}/game/${newGameId}/analytics`);
    }
  };

  // Game switcher dropdown (for all sports)
  const gameSwitcher = dayGames.length > 1 && (
    <div className="flex items-center gap-2 mb-3">
      <label className="text-[10px] text-text-secondary uppercase tracking-wider flex-shrink-0">
        Game
      </label>
      <select
        value={gameId}
        onChange={handleGameSwitch}
        className="flex-1 bg-bg-secondary border border-border/40 rounded-md px-2 py-1.5 text-xs text-text-primary focus:outline-none focus:border-accent-blue/60 appearance-none cursor-pointer"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
      >
        {dayGames.map((g) => {
          const gId = g.id || g.game_id;
          const away = g.away_team_name || g.away_team?.name || g.away_team || 'Away';
          const home = g.home_team_name || g.home_team?.name || g.home_team || 'Home';
          const awayShort = getShortTeamName(away);
          const homeShort = getShortTeamName(home);
          const score = (g.away_score != null && g.home_score != null)
            ? ` (${g.away_score}-${g.home_score})`
            : g.time ? ` ${g.time}` : '';
          return (
            <option key={gId} value={gId}>
              {awayShort} @ {homeShort}{score}
            </option>
          );
        })}
      </select>
    </div>
  );

  // Shared matchup header (reused for both NCAAB and other sports)
  const matchupHeader = (
    <>
      {gameSwitcher}

      {/* Header - Live indicator only */}
      {isLive && (
        <div className="flex items-center justify-end mb-4">
          <Badge variant="danger" size="sm" className="animate-pulse-glow">
            <span className="inline-block w-1.5 h-1.5 bg-white rounded-full mr-1"></span>
            LIVE
          </Badge>
        </div>
      )}

      {/* Compact Game Matchup Header */}
      <Card className="p-3 mb-4">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Away Team */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {game.away_team?.logo && (
              <img
                src={game.away_team.logo}
                alt={game.away_team.name}
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="text-sm font-bold text-text-primary truncate">
                {/* Rank + short name on mobile, rank + full name on desktop */}
                {isNCAAB && game.away_team?.ap_rank && (
                  <span className="text-yellow-400 mr-1">#{game.away_team.ap_rank}</span>
                )}
                <span className="sm:hidden">{getShortTeamName(game.away_team?.name) || 'Away'}</span>
                <span className="hidden sm:inline">{game.away_team?.name || 'Away'}</span>
              </p>
              <p className="text-xs text-text-secondary">
                {game.away_team?.abbreviation}
                {isNCAAB && game.away_team?.conference && (
                  <span className="opacity-60 ml-1">{game.away_team.conference}</span>
                )}
              </p>
            </div>
          </div>

          {/* Center - VS or Score + Date */}
          <div className="text-center flex-shrink-0">
            {isLive || isFinal ? (
              <div className="flex items-center gap-2">
                <span className="text-xl sm:text-2xl font-bold font-mono text-text-primary">
                  {game.away_score ?? '-'}
                </span>
                <span className="text-sm text-text-secondary">-</span>
                <span className="text-xl sm:text-2xl font-bold font-mono text-text-primary">
                  {game.home_score ?? '-'}
                </span>
              </div>
            ) : (
              <div className="text-base sm:text-lg font-bold text-text-secondary">VS</div>
            )}
            <p className="text-[10px] text-text-secondary mt-0.5">
              {new Date(game.date || game.game_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
              {isFinal && <span className="ml-1 opacity-60">Final</span>}
            </p>
          </div>

          {/* Home Team */}
          <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
            <div className="min-w-0 text-right">
              <p className="text-sm font-bold text-text-primary truncate">
                {isNCAAB && game.home_team?.ap_rank && (
                  <span className="text-yellow-400 mr-1">#{game.home_team.ap_rank}</span>
                )}
                <span className="sm:hidden">{getShortTeamName(game.home_team?.name) || 'Home'}</span>
                <span className="hidden sm:inline">{game.home_team?.name || 'Home'}</span>
              </p>
              <p className="text-xs text-text-secondary">
                {isNCAAB && game.home_team?.conference && (
                  <span className="opacity-60 mr-1">{game.home_team.conference}</span>
                )}
                {game.home_team?.abbreviation}
              </p>
            </div>
            {game.home_team?.logo && (
              <img
                src={game.home_team.logo}
                alt={game.home_team.name}
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain flex-shrink-0"
              />
            )}
          </div>
        </div>
      </Card>
    </>
  );

  // NCAAB: Single scrollable page (no tabs, no player data)
  if (isNCAAB) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        {matchupHeader}
        <NCAABAnalyticsContent game={game} sport={sport} />
      </div>
    );
  }

  // Other sports: Tab-based layout
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {matchupHeader}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab('team')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-all relative whitespace-nowrap ${
            activeTab === 'team'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Team Analytics
        </button>
        <button
          onClick={() => setActiveTab('players')}
          className={`px-4 sm:px-6 py-3 font-semibold transition-all relative whitespace-nowrap ${
            activeTab === 'players'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Player Analysis
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'team' && (
        <TeamAnalytics game={game} sport={sport} />
      )}
      {activeTab === 'players' && (
        playersLoading ? (
          <Card className="p-8 text-center">
            <div className="animate-pulse flex flex-col items-center gap-4">
              <div className="h-6 w-40 bg-gray-700 rounded"></div>
              <div className="h-4 w-56 bg-gray-700 rounded"></div>
              <p className="text-sm text-gray-400">Loading player analytics...</p>
            </div>
          </Card>
        ) : playersError ? (
          <Card className="p-8 text-center">
            <p className="text-danger">{playersError}</p>
          </Card>
        ) : (
          <GameAnalytics game={{ ...game, ...players }} sport={sport} />
        )
      )}
    </div>
  );
};

export default GameAnalyticsPage;
